// ─── Relevé d'un COMPTE, mois par mois (module PUR) ───────────────
// Le pendant de `occurrences.js` côté COMPTE : celui-ci répond à « qu'ai-je
// prévu ? », celui-là à « qu'est-ce qui a bougé sur mon compte, et combien
// reste-t-il ? ». Aucun accès Firestore ni React — donc testable.
//
// Deux règles portées depuis `soldeCompte` (elles étaient dans `comptes.js`,
// coincées derrière un import Firestore, donc hors de portée des tests) :
//   • les opérations HORS CALCUL comptent ici. Un virement entre ses propres
//     comptes ne pèse pas dans le budget, mais il bouge bien l'argent.
//   • une PROVISION (`paye === false`) n'a pas bougé : elle ne compte pas.
import { moisImputation } from './occurrences.js';

const cent = (n) => Math.round((Number(n) || 0) * 100) / 100;
const abs = (op) => Math.abs(Number(op?.montant) || 0);

// Signe bancaire : un revenu crédite, tout le reste débite.
export const signe = (op) => (op?.sens === 'revenu' ? 1 : -1);

// Une opération pèse-t-elle sur le solde ? (indépendant de la date)
export const bougeLArgent = (op) => !!op && op.paye !== false;

// ── Sélection ────────────────────────────────────────────────────
// Toutes les opérations d'un compte, quel que soit le mois.
export const operationsDuCompte = (operations = [], compteId) =>
  (compteId ? operations.filter(op => op && op.compteId === compteId) : []);

// Celles d'un mois donné ('AAAA-MM'), les plus récentes d'abord.
export function releveDuMois({ operations = [], compteId, mk }) {
  return operationsDuCompte(operations, compteId)
    .filter(op => moisImputation(op) === mk)
    .sort((a, b) => String(dateOp(b)).localeCompare(String(dateOp(a))));
}

export const dateOp = (op) => (op && (op.dateBanque || op.datePaiement || op.date)) || '';

// ── Totaux d'un jeu d'opérations ─────────────────────────────────
// `net` est signé (revenus positifs) ; `entrees`/`sorties` sont des valeurs
// absolues, pour un affichage « + / − » sans avoir à re-signer à la vue.
// Les provisions sont comptées à part : elles sont annoncées, pas encaissées.
export function totauxReleve(ops = []) {
  let entrees = 0, sorties = 0, provisions = 0;
  for (const op of ops) {
    if (!op) continue;
    if (!bougeLArgent(op)) { provisions += abs(op); continue; }
    if (signe(op) > 0) entrees += abs(op); else sorties += abs(op);
  }
  return { entrees: cent(entrees), sorties: cent(sorties), net: cent(entrees - sorties), provisions: cent(provisions), nb: ops.length };
}

// ── Solde ────────────────────────────────────────────────────────
// Solde initial + tout ce qui a bougé depuis la date de référence, borné à une
// date de fin quand on la donne (`jusqua` inclus, format 'AAAA-MM-JJ').
export function soldeCompteA(compte, operations = [], jusqua) {
  if (!compte) return 0;
  const depuis = compte.dateSolde || '';
  const somme = operations.reduce((s, op) => {
    if (!op || op.compteId !== compte.id) return s;
    const d = dateOp(op);
    if (depuis && d < depuis) return s;
    if (jusqua && d > jusqua) return s;
    if (!bougeLArgent(op)) return s;
    return s + signe(op) * abs(op);
  }, 0);
  return cent((Number(compte.soldeInitial) || 0) + somme);
}

// Solde courant : sans borne de fin. C'est l'ancien `soldeCompte`, mot pour
// mot — `comptes.js` le ré-exporte pour ne casser aucun appelant.
export const soldeCompte = (compte, operations = []) => soldeCompteA(compte, operations, '');

// Solde à la FIN du mois affiché : ce que disait le relevé papier ce mois-là.
// Un mois futur donne donc le solde courant, pas une projection.
//
// La borne est `-31` et NON la vraie fin de mois : c'est une comparaison de
// chaînes, et « 2026-02-31 » attrape toute date de février, y compris une date
// malformée qu'on aurait en base. `finDeMois` sert, lui, à l'AFFICHAGE — là il
// faut une date qui existe vraiment.
export const soldeFinDeMois = (compte, operations = [], mk) =>
  soldeCompteA(compte, operations, `${mk}-31`);

// Dernier jour réel du mois ('2026-02' → '2026-02-28'). Le jour 0 du mois
// suivant EST le dernier du mois courant — années bissextiles comprises.
export function finDeMois(mk) {
  const [y, m] = String(mk).split('-').map(Number);
  if (!y || !m) return '';
  const d = new Date(Date.UTC(y, m, 0));
  return d.toISOString().slice(0, 10);
}

// ── Navigation ───────────────────────────────────────────────────
// Les mois qui portent au moins une opération, du plus ancien au plus récent.
// Sert à sauter au mois utile plutôt que de traverser des mois vides.
export function moisAvecOperations(operations = [], compteId) {
  const vus = new Set();
  operationsDuCompte(operations, compteId).forEach(op => {
    const mk = moisImputation(op);
    if (mk) vus.add(mk);
  });
  return [...vus].sort();
}

// Mois le plus récent qui porte des opérations, ou null. Sert à ouvrir l'écran
// sur du contenu : s'ouvrir sur le mois courant montrait une page vide dès que
// le dernier relevé importé datait d'avant.
export const dernierMoisAvecOperations = (operations = [], compteId) =>
  moisAvecOperations(operations, compteId).slice(-1)[0] || null;

// Mois voisin qui porte des opérations (`sens` = -1 avant, +1 après), ou null.
export function moisVoisinAvecOperations(operations = [], compteId, mk, sens = 1) {
  const l = moisAvecOperations(operations, compteId);
  const candidats = sens > 0 ? l.filter(m => m > mk) : l.filter(m => m < mk).reverse();
  return candidats[0] || null;
}
